import { useEffect, useMemo, useState } from "react";
import { ActionIcon, Center, Flex, Loader, Tooltip, useMantineTheme, Badge } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

interface AdminCollaborationsTabProps {
  companyId: string;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  RAZORPAY: "Razorpay",
  WALLET: "Wallet",
};

const PAYMENT_METHOD_COLORS: Record<string, string> = {
  RAZORPAY: "blue",
  WALLET: "green",
};

type CollaborationListItem = {
  id: string;
  companyName: string | null;
  companyId: string; // This is actually GTID from backend
  paymentMethod: string;
};

export default function AdminCollaborationsTab({ companyId }: AdminCollaborationsTabProps) {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [collaborations, setCollaborations] = useState<CollaborationListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollaborations = async () => {
      setLoading(true);
      try {
        const res = await trpc.admin.getCompanyCollaborations.query({
          companyId,
        });

        setCollaborations(
          res.collaborations.map((c: any) => ({
            id: c.id,
            companyName: c.companyName,
            companyId: c.companyId, // This is GTID
            paymentMethod: c.paymentMethod,
          }))
        );
      } catch (err) {
        handleTrpcError(err);
        setCollaborations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborations();
  }, [companyId]);

  const columns: MRT_ColumnDef<CollaborationListItem>[] = useMemo(
    () => [
      {
        accessorKey: "companyName",
        header: "Company Name",
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "companyId",
        header: "Company ID",
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return value || "—";
        },
      },
      {
        accessorKey: "paymentMethod",
        header: "Payment Method",
        size: 150,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          const color = PAYMENT_METHOD_COLORS[value] || "gray";
          const label = PAYMENT_METHOD_LABELS[value] || value;
          return (
            <Badge variant="light" color={color} size="sm">
              {label}
            </Badge>
          );
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <Center h="300px">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%">
      <MantineReactTable
        columns={columns}
        data={collaborations}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Tooltip label="View Company">
            <ActionIcon
              c="#475569"
              variant="transparent"
              onClick={() => navigate(`/admin/companies/${row.original.id}`)}
            >
              <IconEye />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "400px",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No collaborations found for this company.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
